/** @jsx jsx */
import { jsx } from '@emotion/react';

import { type ReactNode } from 'react';

import { PageConfig_PageName as PageName } from '__generated__/graphql-types';

import SkillEntityContext from 'bundles/browse-common/contexts/SkillEntityContext';
import RelatedChildSkillsProvider from 'bundles/browse-common/providers/RelatedChildSkillsProvider';
import RelatedRolesBySkillProvider from 'bundles/browse-common/providers/RelatedRolesBySkillProvider';
import SkillEntityProvider from 'bundles/browse-common/providers/SkillEntityProvider';
import SkillPartnerListProvider from 'bundles/browse-common/providers/SkillPartnerListProvider';
import SkillSearchProvider from 'bundles/browse-common/providers/SkillSearchProvider';
import SkillValuePropositionProvider from 'bundles/browse-common/providers/SkillValuePropositionProvider';
import TypedComponentsConfigProvider from 'bundles/browse-common/providers/TypedComponentsConfigProvider';
import { NotFound } from 'bundles/common/components/withNotFound';

type Props = {
  skillSlug?: string;
  children: ReactNode;
};

const SkillPageProvider = (props: Props) => {
  const { skillSlug, children } = props;

  if (!skillSlug) {
    return <NotFound />;
  }

  return (
    <TypedComponentsConfigProvider
      pageName={PageName.BrowseSkill}
      additionalInput={{ skill_id: skillSlug, skill_name: skillSlug }}
    >
      <RelatedRolesBySkillProvider skillId={skillSlug}>
        <SkillEntityProvider skillSlug={skillSlug}>
          <SkillEntityContext.Consumer>
            {(context) => {
              const skillEntity = context;
              if (!skillEntity) {
                return null;
              }

              return (
                <SkillSearchProvider skill={skillEntity.skill}>
                  <SkillValuePropositionProvider skill={skillEntity.skill}>
                    <RelatedChildSkillsProvider skill={skillEntity.skill}>
                      <SkillPartnerListProvider>{children}</SkillPartnerListProvider>
                    </RelatedChildSkillsProvider>
                  </SkillValuePropositionProvider>
                </SkillSearchProvider>
              );
            }}
          </SkillEntityContext.Consumer>
        </SkillEntityProvider>
      </RelatedRolesBySkillProvider>
    </TypedComponentsConfigProvider>
  );
};

export default SkillPageProvider;
